import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FiMenu, FiX } from "react-icons/fi";
import { useScrollY } from "../components/Reveal";
import { useSteffiCopy } from "../content";
import { usePublicLang } from "../../../config/i18n/public";

const LANGS = ["de", "en"];
const SHOW_BLOG_NAV = true;

/* The care bar opens transparent over a photographic hero, where the cream
   palette's near-black ink all but disappears. While it is unlifted it borrows
   a light tone instead; the moment it lifts onto its own cream surface the
   normal ink returns. */
const PHOTO_TONE = {
  primary: { color: "#fff", textShadow: "0 1px 12px rgba(0,0,0,.45)" },
  secondary: { color: "rgba(255,255,255,.88)", textShadow: "0 1px 10px rgba(0,0,0,.4)" },
  outline: {
    color: "rgba(255,255,255,.88)",
    borderColor: "rgba(255,255,255,.38)",
    textShadow: "0 1px 10px rgba(0,0,0,.4)",
  },
};

/** Blog and Anmelden do not fit the bar on a phone — measured, 41px are left
 *  and "Blog" alone needs 58. Below `md` they live in this menu; above it they
 *  are written out in the bar, so neither ever appears twice. */
function CompactMenu({ items, label, onPhoto = false }) {
  const [open, setOpen] = useState(false);
  const wrapper = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => event.key === "Escape" && setOpen(false);
    const onPointer = (event) => {
      if (!wrapper.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open]);

  return (
    <div ref={wrapper} className="relative md:hidden">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="menu"
        className="grid h-10 w-10 place-items-center rounded-full transition-colors duration-500 hover:bg-[color:var(--bg-2)] hover:text-[color:var(--color-accent)]"
        style={onPhoto ? PHOTO_TONE.secondary : { color: "var(--ink-2)" }}
      >
        {open ? <FiX className="h-5 w-5" aria-hidden /> : <FiMenu className="h-5 w-5" aria-hidden />}
      </button>
      {open && (
        <div
          role="menu"
          className="steffi-menu absolute right-0 top-[calc(100%+8px)] min-w-[11rem] overflow-hidden rounded-2xl border border-[color:var(--border)] bg-[color:var(--card)] p-1.5"
          style={{ boxShadow: "0 24px 50px -28px rgba(36,33,30,.4)" }}
        >
          {items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              role="menuitem"
              onClick={() => setOpen(false)}
              className={`block rounded-xl px-4 py-3 text-[0.9375rem] font-medium transition-colors duration-300 ${
                item.featured === "family"
                  ? "border border-[color:var(--border)] bg-[color:var(--color-background)] text-[color:var(--ink-2)] hover:bg-[color:var(--bg-2)]"
                  : item.featured
                  ? "bg-[color:color-mix(in_srgb,var(--color-highlight)_45%,white)] text-[color:var(--color-primary)] hover:bg-[color:color-mix(in_srgb,var(--color-highlight)_60%,white)]"
                  : "text-[color:var(--ink-2)] hover:bg-[color:var(--bg-2)] hover:text-[color:var(--color-accent)]"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Nav({ variant = "family" }) {
  const y = useScrollY();
  const lifted = y > 24;
  const isCare = variant === "care";
  /* transparent bar sitting on the hero photograph */
  const onPhoto = isCare && !lifted;
  const { NAV } = useSteffiCopy();
  const { lang, setLang } = usePublicLang();
  const otherLang = LANGS.find((code) => code !== lang) || LANGS[0];

  return (
    <header
      className="fixed inset-x-0 top-0 z-[90] transition-[background-color,box-shadow,backdrop-filter] duration-700"
      style={{
        transitionTimingFunction: "cubic-bezier(.2,.8,.2,1)",
        backgroundColor: lifted ? "color-mix(in srgb, var(--bg) 72%, transparent)" : "transparent",
        backdropFilter: lifted ? "blur(18px) saturate(140%)" : "none",
        boxShadow: lifted ? "0 1px 0 var(--border)" : "none",
      }}
    >
      <div className="mx-auto flex h-[61px] w-full max-w-[1440px] items-center justify-between px-6 lg:px-10">
        <Link to="/" className="flex items-center gap-3 transition-opacity duration-500 hover:opacity-70">
          <img src="/steffi/logo.png" alt="" aria-hidden className="brand-logo h-9 w-9" />
          <span
            className="display wordmark text-[1.125rem] tracking-[-0.01em] transition-colors duration-500"
            style={{ fontWeight: 700, ...(onPhoto ? PHOTO_TONE.primary : null) }}
          >
            SteffiCare
          </span>
        </Link>

        <nav className="flex items-center gap-2">
          <Link
            to="/ueber-uns"
            className="hidden rounded-full px-5 py-2.5 text-[0.9375rem] font-medium text-[color:var(--ink-2)] transition-colors duration-500 hover:text-[color:var(--color-accent)] md:inline-flex md:order-first"
          >
            {NAV.about}
          </Link>
          {SHOW_BLOG_NAV && (
            <Link
              to="/blog"
              className="hidden rounded-full px-5 py-2.5 text-[0.9375rem] font-medium text-[color:var(--ink-2)] transition-colors duration-500 hover:text-[color:var(--color-accent)] md:inline-flex md:order-1"
            >
              {NAV.blog}
            </Link>
          )}

          <Link
            to="/pflegeeinrichtungen"
            className="hidden rounded-full bg-[color:color-mix(in_srgb,var(--color-highlight)_45%,white)] px-5 py-2.5 text-[0.9375rem] font-semibold text-[color:var(--color-primary)] shadow-sm transition hover:bg-[color:color-mix(in_srgb,var(--color-highlight)_60%,white)] md:inline-flex md:order-2"
          >
            {NAV.careHomes}
          </Link>
          {isCare ? (
            <Link to="/termin" className="btn btn-primary !px-6 !py-2.5 !text-[0.9375rem] md:order-5">
              {NAV.contact}
            </Link>
          ) : (
            <Link
              to="/reservierung"
              className="nav-reserve btn btn-primary !px-6 !py-2.5 !text-[0.9375rem] md:order-5"
            >
              {NAV.cta}
            </Link>
          )}

          {/* On a phone the bar is full — logo, CTA and menu leave 41px. The pill
              for the language you are already reading carries no information
              there, so only the other one is offered; it says what a tap does. */}
          <button
            type="button"
            onClick={() => setLang(otherLang)}
            aria-label={NAV.language}
            className="ml-1 w-[40px] rounded-full border py-1.5 text-center text-[0.8125rem] font-semibold uppercase tracking-[0.08em] transition-colors duration-500 hover:text-[color:var(--color-accent)] md:hidden"
            style={
              onPhoto
                ? PHOTO_TONE.outline
                : { color: "var(--ink-2)", borderColor: "var(--border)" }
            }
          >
            {otherLang}
          </button>

          {/* two plain words rather than a dropdown: with only two languages a
              menu costs a click and tells the visitor less. Written open as
              "DE | EN" so the bar reads as one row of text with the call to
              action as its only filled shape. */}
          <div
            className="ml-1 hidden items-center md:flex md:order-4"
            role="group"
            aria-label={NAV.language}
          >
            {LANGS.map((code, i) => {
              const current = code === lang;
              return (
                <React.Fragment key={code}>
                  {i > 0 && (
                    <span aria-hidden className="text-[0.8125rem] text-[color:var(--ink-3)]">
                      |
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => setLang(code)}
                    aria-pressed={current}
                    className="px-2 py-1.5 text-[0.8125rem] font-semibold uppercase tracking-[0.08em] transition-colors duration-500 hover:text-[color:var(--color-accent)]"
                    style={{ color: current ? "var(--ink-2)" : "var(--ink-3)" }}
                  >
                    {code}
                  </button>
                </React.Fragment>
              );
            })}
          </div>

          <Link
            to="/family/login"
            className="hidden rounded-full px-5 py-2.5 text-[0.9375rem] font-medium text-[color:var(--ink-2)] transition-colors duration-500 hover:text-[color:var(--color-accent)] md:inline-flex md:order-3"
          >
            {NAV.login}
          </Link>

          <CompactMenu
            onPhoto={onPhoto}
            label={NAV.menu}
            items={[
              { to: "/ueber-uns", label: NAV.about },
              ...(SHOW_BLOG_NAV ? [{ to: "/blog", label: NAV.blog }] : []),
              { to: isCare ? "/login" : "/family/login", label: NAV.login },
              isCare
                ? { to: "/angehoerige", label: NAV.families, featured: "family" }
                : { to: "/pflegeeinrichtungen", label: NAV.facilities, featured: true },
            ]}
          />
        </nav>
      </div>
    </header>
  );
}
